class FreeGiftHandler extends HTMLElement {
  constructor() {
    super();
    this._adding = false;
    this._onClick = this.handleButtonClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector("[data-button]");
    if (this.button) this.button.addEventListener("click", this._onClick);

    const q = (this.getAttribute("data-qualified") || "").toLowerCase();
    if (q === "false" || q === "0" || q === "no") {
      this.removeGiftIfInCart().catch(console.error);
    } else {
      this.syncGiftQuantity?.().catch(console.error);
    }
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener("click", this._onClick);
  }

  getDesiredQty() {
    const raw = this.getAttribute("data-sync-amounts");
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 1; // fallback to 1
  }

  async syncGiftQuantity() {
    const variantID = this.getAttribute("data-variant-id");
    if (!variantID) return;

    const desired = this.getDesiredQty();

    const cart = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    }).then((r) => r.json());
    const item = cart.items?.find(
      (i) => String(i.variant_id) === String(variantID)
    );
    if (!item) return; // nothing to sync

    if (item.quantity !== desired) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ id: item.key, quantity: desired }),
      });
      document.dispatchEvent(new CustomEvent("cart:build"));
    }
  }

  async handleButtonClick(e) {
    e?.preventDefault?.();
    if (this._adding) return;

    this._adding = true;
    this.setBusy(true);

    try {
      await this.addProduct();
      this.dispatchEvent(new CustomEvent("freegift:added", { bubbles: true }));
      document.dispatchEvent(new CustomEvent("cart:build"));
    } catch (err) {
      console.error("[FreeGift] add failed:", err);
      this.dispatchEvent(
        new CustomEvent("freegift:error", { bubbles: true, detail: err })
      );
    } finally {
      this.setBusy(false);
      this._adding = false;
      if (location.pathname.includes("/cart")) location.reload();
    }
  }

  setBusy(isBusy) {
    if (!this.button) return;
    this.button.disabled = !!isBusy;
    this.button.setAttribute("aria-busy", isBusy ? "true" : "false");
    const originalText =
      this.button.getAttribute("data-text") || this.button.textContent;
    if (!this.button.hasAttribute("data-text"))
      this.button.setAttribute("data-text", originalText);
    this.button.textContent = isBusy
      ? this.button.getAttribute("data-loading-text") || "Wird hinzugefügt…"
      : this.button.getAttribute("data-text");
  }

  async addProduct() {
    const variantID = this.getAttribute("data-variant-id");
    if (!variantID) throw new Error("No Valid ID Provided for gift");

    try {
      const cart = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      }).then((r) => r.json());
      const exists = cart.items?.some(
        (i) => String(i.variant_id) === String(variantID)
      );
      if (exists) {
        await this.syncGiftQuantity();
        return;
      }
    } catch {}

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantID),
            quantity: this.getDesiredQty(),
            properties: { _free_gift: "true" },
          },
        ],
      }),
    });

    if (!res.ok) {
      let message = `Add failed (${res.status})`;
      try {
        const data = await res.json();
        message = data?.description || data?.message || message;
      } catch {}
      throw new Error(message);
    }
    return res.json();
  }

  async removeGiftIfInCart() {
    const variantID = this.getAttribute("data-variant-id");
    if (!variantID) return;

    const cart = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    }).then((r) => r.json());
    const matches =
      cart.items?.filter((i) => String(i.variant_id) === String(variantID)) ||
      [];
    if (!matches.length) return;

    // Remove all matching lines (use id: line_item.key for robustness)
    await Promise.all(
      matches.map((item) =>
        fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        })
      )
    );

    document.dispatchEvent(new CustomEvent("cart:build"));
    if (location.pathname.includes("/cart")) location.reload();
  }
}

if (!window.customElements.get("free-gift-handler")) {
  window.customElements.define("free-gift-handler", FreeGiftHandler);
}


class DonationModule extends HTMLElement {
  constructor() {
    super();
    this._onVariantChange = this.onVariantChange.bind(this);
    this._onAddClick = this.onAddClick.bind(this);
    this._adding = false;
  }

  connectedCallback() {
    this.radios = Array.from(
      this.querySelectorAll('input[type="radio"][name="id"]')
    );

    this.amountInput = this.querySelector('[data-donation-amount]');
    this.atcButton = this.querySelector('[data-add-donation]');

    if (!this.radios.length || !this.atcButton) return;

    // Attach listeners
    this.radios.forEach((r) =>
      r.addEventListener('change', this._onVariantChange)
    );
    this.atcButton.addEventListener('click', this._onAddClick);

    // Initial state
    const checked = this.radios.find((r) => r.checked) || this.radios[0];
    if (checked) {
      checked.checked = true;
      this.selectedVariant = checked.value;
      this.setAttribute('data-selected-variant', this.selectedVariant);
      this.updateAmountVisibility(checked);
    }
  }

  disconnectedCallback() {
    if (this.radios) {
      this.radios.forEach((r) =>
        r.removeEventListener('change', this._onVariantChange)
      );
    }
    if (this.atcButton) {
      this.atcButton.removeEventListener('click', this._onAddClick);
    }
  }

  updateAmountVisibility(radio) {
    if (!this.amountInput) return;

    const isIndividual = radio.hasAttribute('data-individual');

    // Hide / show amount input
    //this.amountInput.style.display = isIndividual ? '' : 'none';

    if (!isIndividual) {
      this.amountInput.value = '';
    } else if (!this.amountInput.value) {
      this.amountInput.value = '1';
    }
  }

  onVariantChange(event) {
    const radio = event.target;
    if (!radio) return;

    this.selectedVariant = radio.value;
    this.setAttribute('data-selected-variant', this.selectedVariant);
    this.updateAmountVisibility(radio);
  }

  async onAddClick(event) {
    event.preventDefault();
    if (this._adding) return;

    const radio = this.radios.find((r) => r.checked);
    if (!radio) return;

    let variantId = radio.value;
    let quantity = 1;

    if (radio.hasAttribute('data-individual') || this.amountInput?.value !== "") {

      const value = parseInt(this.amountInput.value, 10);
      if (!value || value < 1) {
        this.amountInput.focus();
        return;
      }
      quantity = value;
      let individualVariantRadio = this.radios.filter((radio) => radio.hasAttribute("data-individual"));
      variantId = individualVariantRadio[0].value;
    }

    this._adding = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity,
        }),
      });

      if (!res.ok) throw new Error('Cart add failed');
      document.dispatchEvent(new CustomEvent('cart:build'));
      this._adding = false;
    } catch (err) {
      console.error('[Donation] add failed:', err);
    }
  }
}

customElements.define('donation-module', DonationModule);

class ScrollButtons extends HTMLElement {
  connectedCallback() {
    const targetSelector = this.getAttribute('target');
    const itemSelector   = this.getAttribute('scroll-item-selector');

    this._container = document.querySelector(targetSelector);
    this._itemSel   = itemSelector;
    this._prev      = this.querySelector('.prev-button');
    this._next      = this.querySelector('.next-button');

    if (!this._container || !this._prev || !this._next) return;

    this._prev.addEventListener('click', () => this._scroll(-1));
    this._next.addEventListener('click', () => this._scroll(1));

    this._container.addEventListener('scroll', () => this._update(), { passive: true });
    new ResizeObserver(() => this._update()).observe(this._container);

    this._update();
  }

  _getItemWidth() {
    const item = this._container.querySelector(this._itemSel);
    if (!item) return this._container.offsetWidth;
    const style = getComputedStyle(this._container);
    const gap = parseFloat(style.columnGap || style.gap || 0) || 0;
    return item.offsetWidth + gap;
  }

  _scroll(dir) {
    this._container.scrollBy({
      left: dir * this._getItemWidth(),
      behavior: 'smooth'
    });
  }

  _update() {
    const c = this._container;
    const hasOverflow = c.scrollWidth > c.clientWidth + 1;

    this.hidden = !hasOverflow;
    if (!hasOverflow) return;

    const atStart = c.scrollLeft <= 1;
    const atEnd   = c.scrollLeft + c.clientWidth >= c.scrollWidth - 1;

    this._prev.disabled = atStart;
    this._next.disabled = atEnd;
  }
}

customElements.define('scroll-buttons', ScrollButtons);

class SellingPlanGroup extends HTMLElement {
  constructor() {
    super();
    this._onVariantChangeEvent = this._handleVariantChangeEvent.bind(this);
  }

  connectedCallback() {
    this._init();

    const { sectionId, productId, url, variantId } = this.dataset;

    this._abortController = new AbortController();

    if (sectionId && productId) {
      document.addEventListener(
        `variant:change:${sectionId}:${productId}`,
        this._onVariantChangeEvent,
        { signal: this._abortController.signal }
      );
    }

    // On pageload, fetch this variant's selling plans fresh and re-render.
    if (url && variantId) {
      this._fetchAndRender(variantId);
    }
  }

  disconnectedCallback() {
    this._teardown();
    this._abortController?.abort();
  }

  _init() {
    this._select = this.querySelector('.selling-plan-list select');
    this._onetime = this.querySelector('#selling_plan_onetime');
    this._multi = this.querySelector('#selling_plan_multi');

    if (!this._select || !this._multi) return;

    this._onChange = this._handleChange.bind(this);
    this.querySelectorAll('input[name="selling_plan"]').forEach((radio) =>
      radio.addEventListener('change', this._onChange)
    );

    // When the select is disabled and user clicks its container, auto-select multi radio
    this._selectContainer = this.querySelector('.selling-plan-list');
    if (this._selectContainer) {
      this._onContainerClick = () => {
        if (this._select.disabled) {
          this._multi.checked = true;
          this._multi.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      this._selectContainer.addEventListener('click', this._onContainerClick);
    }

    this._syncSelect();
  }

  _teardown() {
    if (this._onChange) {
      this.querySelectorAll('input[name="selling_plan"]').forEach((radio) =>
        radio.removeEventListener('change', this._onChange)
      );
    }
    if (this._selectContainer && this._onContainerClick) {
      this._selectContainer.removeEventListener('click', this._onContainerClick);
    }
  }

  _handleChange() {
    this._syncSelect();
  }

  _syncSelect() {
    const multiSelected = this._multi?.checked;
    this._select.disabled = !multiSelected;

    if (multiSelected && !this._select.value) {
      this._select.selectedIndex = 0;
    }
  }

  // Reuses the section HTML the variant-picker already fetched for the new
  // variant, instead of firing a second network request.
  _handleVariantChangeEvent({ detail }) {
    const { html, variant } = detail || {};
    if (!variant) return;

    if (html) {
      this._renderFromDocument(html);
    } else {
      this._fetchAndRender(variant.id);
    }
  }

  async _fetchAndRender(variantId) {
    const { url, sectionId } = this.dataset;
    if (!url || !sectionId) return;

    try {
      const response = await fetch(`${url}?variant=${variantId}&section_id=${sectionId}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      this._renderFromDocument(doc);
    } catch (err) {
      console.error('[SellingPlanGroup] fetch failed:', err);
    }
  }

  _renderFromDocument(doc) {
    const source = doc.querySelector(
      `selling-plan-group[data-section-id="${this.dataset.sectionId}"]`
    );

    this._teardown();

    if (source) {
      this.innerHTML = source.innerHTML;
      if (source.dataset.variantId) this.dataset.variantId = source.dataset.variantId;
    } else {
      this.innerHTML = '';
    }

    this._init();
  }
}

if (!window.customElements.get('selling-plan-group')) {
  window.customElements.define('selling-plan-group', SellingPlanGroup);
}
